import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ACCENT, Field, PrimaryButton, SecondaryButton, inputClass, ringStyle } from "../artistStudio/shared";
import EvidenceUploader from "./EvidenceUploader";
import { DISPLAY_STATUS_INFO, STATUS_INFO, TONE_CLASS, claimErrorText, fieldErrors, formatDay, formatDayTime, rightsClaimApi } from "./rightsClaimApi";

// `display` (Pending / Accepted / Disputed / Resolved) is used for ownership claims; other cases show their review status.
export function StatusChip({ status, display }) {
  const info = (display && DISPLAY_STATUS_INFO[display]) || STATUS_INFO[status] || { label: status, tone: "gray" };
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TONE_CLASS[info.tone]}`}>{info.label}</span>;
}

const ACTION_LABEL = {
  created: "Case received",
  status_changed: "Status changed",
  evidence_added: "Evidence added",
  counter_notice_received: "Counter-notice received",
  info_requested: "More information requested",
  message: "Message",
  escalated: "Sent to a senior reviewer",
  closed: "Case closed",
  response_accepted: "Artist accepted the claim",
  response_disputed: "Artist disputed the claim",
  auto_escalated: "Sent to our rights team (no response in time)",
  assigned: "Assigned to a reviewer",
};
const PARTY_LABEL = { claimant: "Filer", respondent: "Sound owner", current_owner: "Sound owner", artist: "Artist", admin: "Nyawoo" };

export function CounterNoticeForm({ caseId, onDone }) {
  const [form, setForm] = useState({ statement: "", evidence: [], declaration_accepted: false });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const local = {};
    if (form.statement.trim().length < 20) local.statement = "Explain why the notice is mistaken, in at least 20 characters.";
    if (!form.declaration_accepted) local.declaration_accepted = "Please confirm the declaration to continue.";
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const created = await rightsClaimApi.counterNotice({ notice_id: caseId, statement: form.statement.trim(), evidence: form.evidence, declaration_accepted: true });
      toast.success(`Counter-notice sent (${created.case_number || `#${created.id}`}).`);
      onDone();
    } catch (e) {
      const fields = fieldErrors(e);
      if (Object.keys(fields).length) setErrors(fields);
      else toast.error(claimErrorText(e, "We couldn't send your counter-notice. Please try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 rounded-2xl border border-gray-200 p-4" data-testid="counter-form">
      <h3 className="font-bold text-gray-900">Send a counter-notice</h3>
      <p className="mt-1 text-sm text-gray-600">Use this if you believe the notice is mistaken or you have the right to use the work.</p>
      <div className="mt-3 space-y-4">
        <Field label="Your statement" required error={errors.statement}>
          <textarea value={form.statement} onChange={(e) => setForm((f) => ({ ...f, statement: e.target.value }))} rows={4} maxLength={5000} className={inputClass(!!errors.statement)} style={ringStyle} />
        </Field>
        <Field label="Evidence (optional)" error={errors.evidence}>
          <EvidenceUploader value={form.evidence} onChange={(evidence) => setForm((f) => ({ ...f, evidence }))} disabled={busy} />
        </Field>
        <div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.declaration_accepted} onChange={(e) => setForm((f) => ({ ...f, declaration_accepted: e.target.checked }))} className="mt-1" />
            <span>I confirm, in good faith, that the notice was filed by mistake or that I have the right to use this work.</span>
          </label>
          {errors.declaration_accepted && <p className="mt-1 text-sm text-red-600">{errors.declaration_accepted}</p>}
        </div>
        <PrimaryButton onClick={send} disabled={busy} className="!py-2.5 text-sm">{busy ? "Sending…" : "Send counter-notice"}</PrimaryButton>
      </div>
    </div>
  );
}

// One case: what was filed, the evidence on file, the history the rights team has made visible, and the actions the
// person may take (add evidence; answer a notice with a counter-notice).
export default function CaseDialog({ caseId, onClose, onChanged }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [adding, setAdding] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [counter, setCounter] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    rightsClaimApi.detail(caseId).then(
      (data) => setState({ loading: false, data, error: "" }),
      (e) => setState({ loading: false, data: null, error: claimErrorText(e, "We couldn't load this case.") })
    );
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const sendEvidence = async () => {
    if (!evidence.length) { toast.error("Add at least one file."); return; }
    setBusy(true);
    try {
      const data = await rightsClaimApi.addEvidence(caseId, { evidence, message: message.trim() || undefined });
      setState({ loading: false, data, error: "" });
      setEvidence([]); setMessage(""); setAdding(false);
      toast.success("Evidence added.");
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(claimErrorText(e, "We couldn't add this. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const c = state.data;
  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4" role="dialog" aria-modal="true" aria-labelledby="case-title" data-testid="case-dialog">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-xl max-h-[90vh] overflow-auto">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-gray-500 hover:text-gray-900">×</button>
          <h2 id="case-title" className="flex-1 text-center text-lg font-bold text-gray-900 pr-6">{c ? `${c.claim_label} ${c.case_number || `#${c.id}`}` : "Case"}</h2>
        </div>
        {state.loading && !c && <p className="mt-6 text-center text-sm text-gray-500">Loading…</p>}
        {state.error && <p className="mt-6 text-center text-sm text-red-600" role="alert">{state.error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button></p>}
        {c && (
          <div className="mt-4 text-sm text-gray-800 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <StatusChip status={c.status} display={c.claim_type === "ownership_dispute" ? c.display_status : null} />
              <span className="text-gray-600">Sound: <span className="font-semibold text-gray-900">{c.track?.title || `#${c.track?.id}`}</span></span>
              <span className="text-gray-600">Received {formatDay(c.received_at)}</span>
              {c.dispute_type && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 capitalize">{c.dispute_type}</span>}
            </div>

            {c.needs_response && (
              <div className="rounded-xl bg-red-50 px-4 py-3 text-red-800" role="status" data-testid="case-needs-response">
                <p className="font-semibold">We need something from you</p>
                <p className="mt-1">{c.info_requested || "Please add the information or evidence the rights team asked for."}</p>
              </div>
            )}
            {c.outcome && (
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="font-semibold text-gray-900">Outcome: {STATUS_INFO[c.outcome.status]?.label || c.outcome.status}</p>
                {c.outcome.note && <p className="mt-1 text-gray-700">{c.outcome.note}</p>}
              </div>
            )}

            {c.response && (
              <div className="rounded-xl bg-gray-50 px-4 py-3" data-testid="case-response">
                <p className="font-semibold text-gray-900">
                  {c.response.decision === "disputed" ? "The artist disputed this claim" : c.response.outcome === "takedown" ? "The artist accepted the claim and took the track down" : `The artist accepted the claim and offered ${c.response.share_percent}% of the track's earnings`}
                </p>
                {c.response.text && <p className="mt-1 whitespace-pre-wrap text-gray-700">{c.response.text}</p>}
                <p className="mt-1 text-xs text-gray-500">{formatDayTime(c.response.responded_at)}</p>
              </div>
            )}
            {c.can_respond && c.due_at && <p className="text-amber-800 font-semibold" data-testid="case-respond-by">Respond by {formatDay(c.due_at)}. Use Respond in the list to answer.</p>}

            <div>
              <p className="font-bold text-gray-900">{c.claimant_name}{c.role === "filer" ? " (you)" : ""}</p>
              <p className="mt-1 whitespace-pre-wrap text-gray-700">{c.description}</p>
              {c.original_work_url && <p className="mt-2"><a href={c.original_work_url} target="_blank" rel="noreferrer" className="font-semibold hover:underline" style={{ color: ACCENT }}>Original work</a></p>}
            </div>

            <div>
              <p className="font-bold text-gray-900">Evidence</p>
              {c.documents.length === 0 ? <p className="mt-1 text-gray-500">No files added yet.</p> : (
                <ul className="mt-1 divide-y divide-gray-100 rounded-xl border border-gray-200">
                  {c.documents.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 px-4 py-2">
                      <a href={d.url} target="_blank" rel="noreferrer" className="min-w-0 truncate font-medium hover:underline">{d.name || d.url}</a>
                      <span className="shrink-0 text-xs text-gray-500">{d.mine ? "You" : PARTY_LABEL[d.party] || d.party} · {formatDay(d.added_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {c.can_add_evidence && !adding && <SecondaryButton onClick={() => setAdding(true)} className="mt-3 !py-2 text-sm" data-testid="add-evidence">Add evidence</SecondaryButton>}
              {adding && (
                <div className="mt-3 rounded-2xl border border-gray-200 p-4 space-y-3">
                  <EvidenceUploader value={evidence} onChange={setEvidence} disabled={busy} />
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={2000} placeholder="Add a note (optional)" className={inputClass(false)} style={ringStyle} />
                  <div className="flex gap-2">
                    <PrimaryButton onClick={sendEvidence} disabled={busy || !evidence.length} className="!py-2 text-sm">{busy ? "Sending…" : "Send"}</PrimaryButton>
                    <SecondaryButton onClick={() => { setAdding(false); setEvidence([]); setMessage(""); }} disabled={busy} className="!py-2 text-sm">Cancel</SecondaryButton>
                  </div>
                </div>
              )}
            </div>

            {c.can_counter && !counter && <PrimaryButton onClick={() => setCounter(true)} className="!py-2.5 text-sm" data-testid="open-counter">Send a counter-notice</PrimaryButton>}
            {c.can_counter && counter && <CounterNoticeForm caseId={c.id} onDone={() => { setCounter(false); load(); if (onChanged) onChanged(); }} />}

            <div>
              <p className="font-bold text-gray-900">History</p>
              <ol className="mt-2 space-y-2" data-testid="case-history">
                {c.history.map((h) => (
                  <li key={h.id} className="rounded-xl bg-gray-50 px-4 py-2">
                    <p className="font-semibold text-gray-900">{ACTION_LABEL[h.action] || h.action}{h.to_status ? ` - ${STATUS_INFO[h.to_status]?.label || h.to_status}` : ""}</p>
                    {h.message && <p className="text-gray-700">{h.message}</p>}
                    <p className="text-xs text-gray-500">{formatDayTime(h.at)}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
